// LIFE — the top of the canvas. The Mountain is painted by the home behind
// everything (orbit.tsx); here we only choose what sits on it: the ASTRA
// compass or the Mountain controls (the Compass ⇄ Mountain pill). The compass
// is PINNED: its disc centre sits on the mountain's peak and never moves or
// scrolls. This screen holds only the world, the compass, its D-pad and the
// mode pill; the gold connector drops off the bottom edge toward the ORB
// screen below (a gold ▼ takes you there).
import React, { useEffect, useRef } from 'react';
import { Animated, Pressable, StyleSheet, Text, View } from 'react-native';
import { CompassCore, type OrbCtx } from '../olmps/CompassView';
import type { GearId } from '../olmps/ascension';
import type { NowStep } from './useCurrentStep';
import type { Sector, Stage } from '../domain/olmps';
import type { OracleProject } from '../olmps/useOracleProjects';
import type { OracleSpine } from '../olmps/oracle-spine-data';
import type { Orbit } from './orbit';
import { MountainView } from './MountainView';
import { LinearGradient } from 'expo-linear-gradient';
import { GlassFill } from './ui';
import { makeStyles, useV10 } from './theme';

export type LifeMode = 'compass' | 'mountain';
/** Fraction of the orbit frame's height at which the summit sits. */
export const PEAK_FRAC = 0.37;

export function LifeView({
  userId,
  current,
  projects,
  spine,
  orbit,
  frameTop,
  frameSize,
  pillTop,
  bottomY,
  mode,
  onMode,
  onOpenTool,
  onOrbCtx,
  onNodePick,
  nodeText,
  frameLeft,
  anchorY,
  bottomInset,
  defaultGear,
}: {
  userId: string | undefined;
  current: NowStep | null;
  projects: Record<Sector, OracleProject[]>;
  spine: OracleSpine | null;
  orbit: Orbit;
  /** Top of the orbit frame and its size, in this view's (= the panel's) coordinates. */
  frameTop: number;
  frameSize: number;
  pillTop: number;
  /** Lowest y the orb may reach. */
  bottomY: number;
  mode: LifeMode;
  onMode: (m: LifeMode) => void;
  onOpenTool: (tool: string) => void;
  onOrbCtx: (ctx: OrbCtx) => void;
  /** A compass node was tapped — go to the orb. */
  onNodePick: () => void;
  nodeText: (sector: Sector | null, stage: Stage) => string | null;
  /** x of the (screen-wide or wider) orbit frame. */
  frameLeft: number;
  /** Peak y: where the compass disc centre sits. */
  anchorY: number;
  bottomInset: number;
  defaultGear: GearId;
}) {
  const t = useV10();
  const styles = useStyles();

  // The disc fades with the mode; the orb and the mountain behind stay put.
  const discOpacity = useRef(new Animated.Value(mode === 'compass' ? 1 : 0)).current;
  useEffect(() => {
    Animated.timing(discOpacity, { toValue: mode === 'compass' ? 1 : 0, duration: 420, useNativeDriver: true }).start();
  }, [mode, discOpacity]);

  return (
    <View style={StyleSheet.absoluteFill} pointerEvents="box-none">
      {mode === 'mountain' ? (
        <View style={{ position: 'absolute', top: 0, bottom: 0, left: frameLeft, width: frameSize }} pointerEvents="box-none">
          <MountainView orbit={orbit} frameTop={frameTop} frameSize={frameSize} projects={projects} spine={spine} current={current} onOpenTool={onOpenTool} onOrbCtx={onOrbCtx} onExplore={onNodePick} />
        </View>
      ) : null}
      {/* Hidden compass must not swallow the Mountain's taps (D-pad, stops, markers). */}
      <View style={StyleSheet.absoluteFill} pointerEvents={mode === 'compass' ? 'box-none' : 'none'}>
      <CompassCore
        userId={userId}
        onOpenTool={onOpenTool}
        compact={false}
        pinned
        onOrbCtx={onOrbCtx}
        onNodePick={onNodePick}
        nodeText={nodeText}
        anchorY={anchorY}
        bottomY={bottomY}
        discOpacity={discOpacity}
        discHidden={mode !== 'compass'}
        defaultSector={current?.sector ?? null}
        defaultGear={defaultGear}
      />
      </View>
      <View style={[styles.modeRow, { top: pillTop }]}>
        <GlassFill radius={999} />
        {(['compass', 'mountain'] as LifeMode[]).map((m) => (
          <Pressable key={m} onPress={() => onMode(m)} style={[styles.modeBtn, mode === m && styles.modeBtnOn]} hitSlop={6}>
            <Text style={[styles.modeText, mode === m && { color: t.onGold }]}>{m === 'compass' ? 'Compass' : 'Mountain'}</Text>
          </Pressable>
        ))}
      </View>
    </View>
  );
}

const useStyles = makeStyles((t) =>
  StyleSheet.create({
    modeRow: { position: 'absolute', right: 10, flexDirection: 'row', gap: 6, padding: 4, borderRadius: 999, borderWidth: 1, borderColor: t.navyLine, height: 36, overflow: 'hidden', zIndex: 20 },
    modeBtn: { paddingHorizontal: 16, justifyContent: 'center', borderRadius: 999 },
    modeBtnOn: { backgroundColor: t.gold, boxShadow: `0 0 14px ${t.goldGlow}` } as object,
    modeText: { color: t.ivorySoft, fontSize: 12, fontWeight: '800', letterSpacing: 0.5 },
  }),
);
